<script lang="ts" setup>
import {computed} from 'vue';
import type {ImageGrid} from '@/ts/types/dto/interventions-dto';
import {useScreenSize} from '@/ts/composables/stateful/use-screen-size';
import {CAP_GRID_TO_IMAGES} from '@/ts/utils/consts';
import {useLogger} from '@/ts/composables/pure/use-logger';
import LazyImage from '@/vue/atoms/lazy-image.vue';

const props = defineProps<{ imageGrid: ImageGrid }>();
const {isMobileResolution} = useScreenSize();
const logger = useLogger('ImageGrid');

interface GridDims {
  rows: number;
  cols: number;
  parsedFromDisplay: boolean; // true if successfully parsed from display string
}
const GRID_RxC_REGEX = /^\s*(?<rows>\d+)\s*[xX]\s*(?<cols>\d+)\s*$/u;

/**
 * Parse "RxC" string; if invalid, derive a near-square grid from imageCount.
 */
function parseGrid(display: string | undefined, imageCount: number): GridDims {
  try {
    const m = display && GRID_RxC_REGEX.exec(display);
    if (m && m.groups) {
      const rows = Math.max(0, Number(m.groups.rows));
      const cols = Math.max(0, Number(m.groups.cols));
      return {rows, cols, parsedFromDisplay: true};
    }
  } catch (_error) {
    logger.error('Error parsing grid display string', display)();
  }

  // Fallback: calculate from imageCount
  const totalImages = Number.isFinite(imageCount) ? Math.max(0, Math.floor(imageCount)) : 0;
  if (totalImages === 0) {
    return {rows: 0, cols: 0, parsedFromDisplay: false};
  }

  const cols = Math.ceil(Math.sqrt(totalImages));
  const rows = Math.ceil(totalImages / cols);
  return {rows, cols, parsedFromDisplay: false};
}

const gridLayout = computed(() => {
  const images = Array.isArray(props.imageGrid?.images) ? props.imageGrid.images : [];
  return parseGrid(props.imageGrid?.grid_display, images.length);
});

/**
 * Effective CAP_GRID_TO_IMAGES behavior:
 * - Use build config value if parsing succeeded
 * - Force true (cap to available images) if parsing failed to ensure something shows
 */
const shouldCapGrid = computed(() => {
  const layout = gridLayout.value;
  return layout.parsedFromDisplay ? CAP_GRID_TO_IMAGES : true;
});

/** Grid layout class: stack on mobile, respect cols otherwise */
const gridLayoutClass = computed(() => {
  if (isMobileResolution.value) {
    return 'mobile-stack';
  }
  return `desktop-columns cols-${gridLayout.value.cols}`;
});

const aspectRatioClass = computed(() => {
  const {rows, cols} = gridLayout.value;
  if (cols === 1 && rows > 1) {
    return 'portrait-tall';
  }
  if (rows === 1 && cols > 1) {
    return 'landscape-wide';
  }
  if (rows === cols) {
    return 'square';
  }
  return cols > rows ? 'landscape' : 'portrait';
});

/**
 * When shouldCapGrid is:
 * - true  -> up to matrix slots (min(total, [images.length])), no padding.
 * - false -> exactly rows X cols items, pad with null placeholders.
 *
 * shouldCapGrid is smart: uses CAP_GRID_TO_IMAGES when parsing succeeds,
 * but forces true when parsing fails to ensure something shows.
 */
type GridItem = ImageGrid['images'][number] | null;

const displayedItems = computed<GridItem[]>(() => {
  const {rows, cols} = gridLayout.value;
  const total = rows * cols;
  const images = Array.isArray(props.imageGrid?.images) ? props.imageGrid.images : [];

  if (shouldCapGrid.value) {
    const count = Math.min(total, images.length);
    return Array.from({length: count}, (_, i) => images.at(i) ?? null);
  }
  return Array.from({length: total}, (_, i) => images.at(i) ?? null);
});

</script>

<template>
  <div class="image-grid-component">
    <header v-if="props.imageGrid.name || props.imageGrid.description" class="image-grid-header">
      <h2 v-if="props.imageGrid.name" class="image-grid-name">
        {{ props.imageGrid.name }}
      </h2>
      <p v-if="props.imageGrid.description" v-safe-html="props.imageGrid.description" class="image-grid-description"/>
    </header>

    <div class="image-grid" :class="gridLayoutClass">
      <div
        v-for="(item, index) in displayedItems"
        :key="index"
        class="grid-item"
        :class="{'is-placeholder': !item}"
        :aria-hidden="!item ? 'true' : undefined"
      >
        <div class="image-container" :class="aspectRatioClass">
          <template v-if="item && item.image_id">
            <lazy-image
              class="grid-image"
              :src="item.image_id"
              :alt-txt="item.title || item.description || ''"
              loading="lazy"
              :width="item.width"
              :height="item.height"
            />
          </template>
          <template v-else-if="!shouldCapGrid">
            <div class="image-placeholder" aria-hidden="true">
              <span class="placeholder-text">{{ $t('no_image_available') }}</span>
            </div>
          </template>
        </div>

        <template v-if="item">
          <h3 v-if="item.title" class="image-title">
            {{ item.title }}
          </h3>
          <p v-if="item.description" class="image-description">
            {{ item.description }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.image-grid-component { 
  width: 100%; 
  margin: var(--Spacing-spacing-6) 0; 
}

.image-grid-header {
  margin-bottom: var(--Spacing-spacing-4);
}

.image-grid-name {
  @include font-h2;
  margin: 0 0 var(--Spacing-spacing-2) 0;
  color: var(--Text-text-primary);
}

.image-grid-description {
  @include font-body;
  margin: 0;
  color: var(--Text-text-primary);
  
  :deep(strong) { font-weight: 600; }
  :deep(em) { font-style: italic; }
}

.image-grid {
  width: 100%;

  &.mobile-stack {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--Spacing-spacing-4);
  }

  &.desktop-columns {
    display: grid;
    gap: var(--Spacing-spacing-4);

    &.cols-1 { grid-template-columns: repeat(1, 1fr); }
    &.cols-2 { grid-template-columns: repeat(2, 1fr); }
    &.cols-3 { grid-template-columns: repeat(3, 1fr); }
    &.cols-4 { grid-template-columns: repeat(4, 1fr); }
    &.cols-5 { grid-template-columns: repeat(5, 1fr); }
    &.cols-6 { grid-template-columns: repeat(6, 1fr); }
  }
}

.grid-item {
  position: relative;

  &.is-placeholder {
    pointer-events: none;
  }
}

.image-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: var(--Border-radius-border-radius-m);
  background-color: var(--Neutral-UI-neutral-ui-10);

  &.portrait-tall { aspect-ratio: 1 / 1.4; }
  &.landscape-wide { aspect-ratio: 1.4 / 1; }
  &.square { aspect-ratio: 1 / 1; }
  &.landscape { aspect-ratio: 1.3 / 1; }
  &.portrait { aspect-ratio: 1 / 1.3; }
}

.grid-image { width: 100%; height: 100%; object-fit: cover; display: block; }

.image-placeholder {
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%;
  background-color: var(--Neutral-UI-neutral-ui-20);
  border-radius: var(--Border-radius-border-radius-m);
}

.placeholder-text { @include font-body-sm; color: var(--Text-text-secondary); }

.image-title {
  @include font-h3;
  margin-top: var(--Spacing-spacing-3);
  margin-bottom: var(--Spacing-spacing-1);
  color: var(--Text-text-primary);
}

.image-description {
  @include font-body-sm;
  margin-top: var(--Spacing-spacing-2);
  color: var(--Text-text-primary);
}

@include screen-md {
  .image-grid-component { margin: var(--Spacing-spacing-8) 0; }
  .image-description { @include font-body; }
}
</style>
